import { useState, useRef, useEffect } from 'react';
import { Menu, X, ChevronDown, Calendar, User, ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useParticipantCheck } from '../../../hooks/useParticipantCheck';
import { useEventDetails } from '../../../hooks/useEventDetails';
import { AccountDropdown } from '../../components/shared/AccountDropdown';

interface NavbarV1Props {
  eventSlug: string;
  eventName?: string;
  eventDates?: string;
  account?: {
    id: string;
    email: string;
    fullName?: string;
    profilePictureUrl?: string;
  };
  accountLoading?: boolean;
  primaryColor?: string;
  hasDays?: boolean;
  hasContentPages?: boolean;
  contentPages?: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
  contentPagesNavbarName?: string;
  eventOptions?: {
    navigationSettings?: {
      schedule?: {
        name: string;
      };
    };
  };
}

/**
 * V1 Event Navbar - FROZEN
 *
 * This component displays navigation for V1 events only.
 * It is a simplified version that shows:
 * - Details (always)
 * - Schedule/Agenda (if event has days)
 * - Content pages (if any exist)
 * - Registration (if open)
 *
 * DO NOT add V2 features like custom navigation settings.
 * This component will be removed after V1 sunset.
 */
export const NavbarV1: React.FC<NavbarV1Props> = ({
  eventSlug,
  eventName = 'Event',
  eventDates = '',
  account,
  accountLoading = false,
  primaryColor = '#000000',
  hasDays = false,
  hasContentPages = false,
  contentPages = [],
  contentPagesNavbarName = 'More',
  eventOptions
}) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const moreDropdownRef = useRef<HTMLDivElement>(null);

  // Get event details and participant status
  const { data: eventDetails } = useEventDetails(eventSlug);
  const { data: participantCheck } = useParticipantCheck(eventSlug, !!account);

  // Determine if user is a participant
  const isParticipant = participantCheck?.isParticipant || false;

  // Get navigation settings from eventDetails
  const navOptions = eventDetails?.eventOptions;

  // Get registration button text
  const registrationButtonText = navOptions?.registrationName || 'Register';

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target as Node)) {
        setIsMoreDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isActive = (path: string) => {
    // Since Router has basename set to /${eventSlug}, location.pathname won't include it
    const currentPath = location.pathname;

    // For root paths like /details, check for exact match or root redirect
    if (path === '/details') {
      return currentPath === '/details' || currentPath === '/' || currentPath === '';
    }

    // For content pages, check if we're on a pages/* route
    if (path.startsWith('/pages/')) {
      return currentPath === path;
    }

    // For other paths, exact match
    return currentPath === path;
  };

  // Build navigation items dynamically based on show flags
  const buildNavItems = () => {
    const items: Array<{
      key: string;
      path: string;
      label: string;
      show: boolean;
      isExternal?: boolean;
    }> = [];

    // Details - check if enabled
    if (navOptions?.showDetailsNav !== false) {
      items.push({
        key: 'details',
        path: '/details',
        label: navOptions?.detailsName || 'Details',
        show: true
      });
    }

    // Forms - check if enabled (moved between Details and Schedule)
    if (navOptions?.showFormsNav) {
      items.push({
        key: 'forms',
        path: '/forms',
        label: navOptions?.formsNavName || 'Forms',
        show: true
      });
    }

    // Schedule - check if enabled and has days
    if (navOptions?.showScheduleNav !== false && (eventDetails?.hasDays || hasDays)) {
      items.push({
        key: 'schedule',
        path: '/schedule',
        label: navOptions?.scheduleName || 'Schedule',
        show: true
      });
    }

    // Registration - check if enabled
    if (navOptions?.showRegistrationNav) {
      items.push({
        key: 'registration',
        path: `/${eventSlug}/registrations/new`,
        label: navOptions?.registrationName || 'Register',
        show: true,
        isExternal: true // This will be used to render as an anchor tag
      });
    }

    // Expo - check if enabled
    if (navOptions?.showExpoNav) {
      items.push({
        key: 'expo',
        path: '/expo',
        label: navOptions?.expoName || 'Expo',
        show: true
      });
    }

    return items;
  };

  const navItems = buildNavItems();

  // Apply nav_items_order if provided
  const orderedNavItems = (() => {
    if (!navOptions?.navItemsOrder?.length) return navItems;

    const itemsMap = new Map(navItems.map(item => [item.key, item]));
    const orderedItems: typeof navItems = [];
    const addedKeys = new Set<string>();

    // Add items in specified order
    navOptions.navItemsOrder.forEach((orderItem: string | { item: string }) => {
      const key = typeof orderItem === 'string' ? orderItem : orderItem.item;
      const item = itemsMap.get(key);
      if (item && !addedKeys.has(key)) {
        orderedItems.push(item);
        addedKeys.add(key);
      }
    });

    // Add any remaining items
    navItems.forEach(item => {
      if (!addedKeys.has(item.key)) {
        orderedItems.push(item);
      }
    });

    return orderedItems;
  })();

  const visibleNavItems = orderedNavItems.filter(item => item.show);

  // Main nav shows all standard nav items
  const mainNavItems = visibleNavItems;

  // Get content pages from eventDetails or props
  const eventContentPages = eventDetails?.contentPages || contentPages;
  const eventContentPagesNavbarName = eventDetails?.contentPagesNavbarName || contentPagesNavbarName;

  // Content pages always go in the More dropdown
  const hasMoreItems = eventContentPages.length > 0;

  // Determine the logo link destination - fallback to first available nav item
  const getLogoLink = () => {
    // Check if details page is enabled
    if (navOptions?.showDetailsNav !== false) {
      return '/details';
    }

    // Fallback to first available navigation item
    if (mainNavItems.length > 0) {
      // For external links (like registration), we need to use the path as-is
      return mainNavItems[0].isExternal ? mainNavItems[0].path : mainNavItems[0].path;
    }

    // If no main nav items, check content pages
    if (eventContentPages.length > 0) {
      return `/pages/${eventContentPages[0].slug}`;
    }

    // Ultimate fallback
    return '/details';
  };

  const logoLink = getLogoLink();
  const isLogoLinkExternal = logoLink.startsWith('/') && !logoLink.startsWith('/details') && !logoLink.startsWith('/schedule') && !logoLink.startsWith('/pages/') && !logoLink.startsWith('/forms') && !logoLink.startsWith('/expo');

  // Component to render the logo with proper link handling
  const LogoLink: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (isLogoLinkExternal) {
      return (
        <a href={logoLink} className="flex items-center">
          {children}
        </a>
      );
    }
    return (
      <Link to={logoLink} className="flex items-center">
        {children}
      </Link>
    );
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative h-16">
          {/* Three column layout on large screens using grid */}
          <div className="flex h-full lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-4">
            {/* Left side - Logo */}
            <div className="flex items-center flex-1 lg:flex-initial">
              <div className="flex-shrink-0 flex items-center lg:mr-8">
                <LogoLink>
                  {eventDetails?.imageUrl ? (
                    <div className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                      {/* Blurred background */}
                      <div className="absolute inset-0">
                        <img
                          src={eventDetails.imageUrl}
                          alt=""
                          className="w-full h-full object-cover blur-xl scale-110 opacity-20"
                          aria-hidden="true"
                        />
                      </div>
                      {/* Main image */}
                      <div className="relative h-full w-full flex items-center justify-center">
                        <img
                          src={eventDetails.imageUrl}
                          alt={eventName}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  ) : null}
                  <div className="ml-3 hidden sm:block max-w-xs lg:max-w-sm xl:max-w-md">
                    <div className="text-sm font-medium text-gray-900 truncate">{eventName}</div>
                    {eventDates && (
                      <div className="text-xs text-gray-500 truncate">{eventDates}</div>
                    )}
                  </div>
                </LogoLink>
              </div>

              {/* Mobile/Tablet Navigation - shown on smaller screens */}
              <div className="ml-6 flex items-center space-x-1 lg:hidden">
                {mainNavItems.map((item) =>
                  item.isExternal ? (
                    <a
                      key={item.path}
                      href={item.path}
                      className="hidden md:inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors text-gray-700 hover:text-gray-900 hover:bg-gray-100 whitespace-nowrap"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`hidden md:inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                        isActive(item.path)
                          ? 'text-white'
                          : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                      style={isActive(item.path) ? { backgroundColor: primaryColor } : {}}
                    >
                      {item.label}
                    </Link>
                  )
                )}
              </div>
            </div>

            {/* Center - Navigation (hidden on mobile) */}
            <div className="hidden lg:flex lg:items-center lg:justify-center">
              <div className="flex items-center space-x-1">
                {mainNavItems.map((item) =>
                item.isExternal ? (
                  <a
                    key={item.path}
                    href={item.path}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors text-gray-700 hover:text-gray-900 hover:bg-gray-100 whitespace-nowrap"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                      isActive(item.path)
                        ? 'text-white'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    style={isActive(item.path) ? { backgroundColor: primaryColor } : {}}
                  >
                    {item.label}
                  </Link>
                )
              )}

              {/* More dropdown */}
              {hasMoreItems && (
                <div className="relative" ref={moreDropdownRef}>
                  <button
                    onClick={() => setIsMoreDropdownOpen(!isMoreDropdownOpen)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors whitespace-nowrap"
                  >
                    {eventContentPagesNavbarName}
                    <ChevronDown
                      size={16}
                      className={`ml-1 transform transition-transform ${isMoreDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {isMoreDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5">
                      {eventContentPages.map((page) => (
                        <Link
                          key={page.slug}
                          to={`/pages/${page.slug}`}
                          className={`block px-4 py-2 text-sm ${
                            isActive(`/pages/${page.slug}`)
                              ? 'bg-gray-100 text-gray-900 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                          onClick={() => setIsMoreDropdownOpen(false)}
                        >
                          {page.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
              </div>
            </div>

            {/* Right side - Account and Mobile Menu */}
            <div className="flex items-center justify-end flex-1 lg:flex-initial">
            {/* Smart Action Button */}
            {accountLoading ? (
              <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse"></div>
            ) : account ? (
              <>
                {/* Signed-in user */}
                {isParticipant ? (
                  // Show Enter Event button for participants
                  <a
                    href={`/accounts/events/${eventSlug}`}
                    className="text-white px-4 py-2 rounded-md text-sm font-medium transition-colors mr-3 whitespace-nowrap"
                    style={{ backgroundColor: primaryColor }}
                    onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(1)'}
                  >
                    Enter {eventDetails?.team?.eventName || 'Event'}
                    <ArrowRight className="inline-block ml-1 h-4 w-4" />
                  </a>
                ) : null}

                {/* Account dropdown - always show for signed-in users */}
                <AccountDropdown account={account} accountLoading={accountLoading} />
              </>
            ) : (
              <>
                {/* Sign In button for anonymous users */}
                <a
                  href="/accounts/sign_in"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors whitespace-nowrap"
                >
                  <User className="h-4 w-4 mr-1" />
                  Sign In
                </a>
              </>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden ml-4 inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset"
              style={{ focusRingColor: primaryColor }}
            >
              {isMobileMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {/* Main navigation items */}
            {mainNavItems.map((item) =>
              item.isExternal ? (
                <a
                  key={item.path}
                  href={item.path}
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive(item.path)
                      ? 'text-white'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  style={isActive(item.path) ? { backgroundColor: primaryColor } : {}}
                >
                  {item.label}
                </Link>
              )
            )}

            {/* Content pages */}
            {contentPages.length > 0 && (
              <>
                <div className="border-t border-gray-200 my-2"></div>
                {eventContentPages.map((page) => (
                  <Link
                    key={page.slug}
                    to={`/pages/${page.slug}`}
                    className={`block px-3 py-2 rounded-md text-base font-medium ${
                      isActive(`/pages/${page.slug}`)
                        ? 'text-white'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    style={isActive(`/pages/${page.slug}`) ? { backgroundColor: primaryColor } : {}}
                  >
                    {page.name}
                  </Link>
                ))}
              </>
            )}

            {/* Sign In link for anonymous users in mobile menu */}
            {!account && (
              <>
                <div className="border-t border-gray-200 my-2"></div>
                <a
                  href="/accounts/sign_in"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <User className="inline-block h-4 w-4 mr-2" />
                  Sign In
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};